import { getAppConfig, getDeviceDisplayName, type AppId } from '@packages/types/app';
// --- 테이블 ---
import { type ColumnDef } from '@tanstack/react-table';

// --- 공통 UI 컴포넌트 ---
import { Flex, IconButton, TextButton, Tag } from '@/components/ui';

// --- 유틸 ---
import { formatDate, formatDueDateLine } from '@/utils/date';
import { getDeviceImageUrl } from '@/utils/deviceImageUtils';

// --- i18n ---
import { i18nKeys } from '@/i18n/i18nKeyGen';

// --- 사이트 컴포넌트 ---
import { DeviceDetailTab } from '@/components/site/DeviceDetailTab';

// ==================== 타입 정의 ====================

export interface DeviceTableRow {
  app_id?: AppId;
  id: number;
  serial_number: string;
  cloud_name: string | null;
  device_type: string;
  sensor_types: string[];
  /** sensor_types와 동일한 순서/길이 - 각 센서의 비활성화(disabled) 여부 */
  sensor_types_disabled?: boolean[];
  is_connected: boolean;
  calibration_due_date: string | null;
  /** 서버 계산 남은 일수 (GET /api/device/list) */
  cal_due_day_num?: number | null;
  /** MINI: scalar INT, LITE: NULL (use calibration_interval_array) */
  calibration_interval: number | null | undefined;
  /** LITE only: INT[] array, MINI: NULL */
  calibration_interval_array: number[] | null | undefined;
  bump_test_due_date?: string | null;
  bump_due_day_num?: number | null;
  last_connect: string | null;
  [key: string]: any;
}

interface DeviceTableColumnsProps {
  t: (key: string) => string;
  theme: any;
  onEdit: (device: DeviceTableRow) => void;
  onDelete: (device: DeviceTableRow) => void;
}

// ==================== 테이블 컬럼 정의 ====================

/**
 * 디바이스 테이블 컬럼 생성.
 * 컬럼: Device Type, Serial Number, Sensors, Calibration Due Date, Last Connect, Actions
 */
export function createDeviceTableColumns({
  t,
  theme,
  onEdit,
  onDelete,
}: DeviceTableColumnsProps): ColumnDef<DeviceTableRow>[] {
  const textStyle = {
    color: theme.colors.text.primary,
    fontSize: theme.fontSize.sm,
  };

  return [
    // ── 디바이스 타입 컬럼 ──
    {
      accessorKey: 'device_type',
      header: () => t(i18nKeys.company.device_type),
      cell: ({ row }) => {
        const deviceType = (row.original.device_type || '').toLowerCase();
        const imgSrc = getDeviceImageUrl(deviceType, {
          appId: row.original.app_id,
          sensorTypes: row.original.sensor_types,
          isConnected: row.original.is_connected,
        });
        return (
          <Flex align="center" justify="between">
            <img
              src={imgSrc}
              alt={getDeviceDisplayName(deviceType, row.original.app_id)}
              title={getAppConfig(row.original.app_id).displayName}
              style={{
                width: 52,
                height: 52,
                objectFit: 'contain',
                display: 'block',
              }}
              onError={e => {
                const img = e.currentTarget;
                img.onerror = null;
                img.style.visibility = 'hidden';
              }}
            />
            <Tag variant="primary" size="xs">
              {getDeviceDisplayName(row.original.device_type, row.original.app_id)}
            </Tag>
          </Flex>
        );
      },
      meta: {
        style: {
          width: '120px',
          minWidth: '120px',
        },
      },
      enableSorting: true,
      enableColumnFilter: true,
    },
    // ── 시리얼 번호 컬럼 ──
    {
      accessorKey: 'serial_number',
      header: () => t(i18nKeys.site.serial),
      cell: ({ row }) => {
        const isCalibrationOverdue = row.original.calibration_due_date
          ? new Date(row.original.calibration_due_date) < new Date()
          : false;

        const textStyleLocal = isCalibrationOverdue
          ? {
              color: theme.colors.status.danger.fill.surface,
              fontWeight: 700 as const,
            }
          : textStyle;

        return (
          <span style={{ ...textStyleLocal, display: 'block', textAlign: 'center' }}>
            {row.original.serial_number}
          </span>
        );
      },
      enableSorting: true,
      enableColumnFilter: true,
      meta: {
        style: {
          width: '80px',
          minWidth: '80px',
          textAlign: 'center',
        },
      },
    },
    // ── 센서 타입 컬럼 ──
    {
      accessorKey: 'sensor_types',
      header: () => 'Sensors',
      cell: ({ row }) => {
        const sensors: string[] = row.original.sensor_types || [];
        const disabledFlags: boolean[] = row.original.sensor_types_disabled || [];
        if (sensors.length === 0) return <span style={textStyle}>-</span>;
        return (
          <Flex gap={1} wrap>
            {sensors.map((sensor, index) => {
              const isDisabled = disabledFlags[index] ?? false;
              return isDisabled ? (
                <Tag
                  key={sensor}
                  variant="custom"
                  size="xs"
                  backgroundColor={theme.colors.surface.layout.overlay}
                  color={theme.colors.text.disabled}
                  title={t(i18nKeys.site.sensorDisabled)}
                  style={{ textDecoration: 'line-through', opacity: 0.7 }}
                >
                  {sensor}
                </Tag>
              ) : (
                <Tag key={sensor} variant="info" size="xs">
                  {sensor}
                </Tag>
              );
            })}
          </Flex>
        );
      },
      filterFn: (row, _columnId, filterValue: string) => {
        const sensors: string[] = row.original.sensor_types || [];
        return sensors.some(s => s.toLowerCase().includes(filterValue.toLowerCase()));
      },
      meta: {
        style: {
          width: '150px',
          minWidth: '120px',
        },
      },
      enableSorting: false,
      enableColumnFilter: true,
    },
    // ── 교정 만기일 컬럼 ──
    {
      accessorKey: 'calibration_due_date',
      header: () => t(i18nKeys.site.calibrationDueDate),
      cell: ({ row }) => {
        const isCalibrationOverdue = row.original.calibration_due_date
          ? new Date(row.original.calibration_due_date) < new Date()
          : false;

        const textStyleLocal = isCalibrationOverdue
          ? {
              color: theme.colors.status.danger.fill.surface,
              fontWeight: 700 as const,
            }
          : textStyle;

        // LITE: calibration_interval_array, MINI: [calibration_interval]
        const calibInterval =
          row.original.calibration_interval_array ??
          (row.original.calibration_interval != null
            ? [row.original.calibration_interval]
            : undefined);

        return (
          <span style={{ ...textStyleLocal, display: 'block', textAlign: 'center' }}>
            {formatDueDateLine(
              row.original.calibration_due_date,
              row.original.cal_due_day_num,
              calibInterval
            )}
          </span>
        );
      },
      enableSorting: true,
      enableColumnFilter: true,
      meta: {
        style: {
          textAlign: 'center',
        },
      },
    },
    // ── Bump Test Due Date 컬럼 ──
    {
      accessorKey: 'bump_test_due_date',
      header: () => 'Bump Test Due',
      cell: ({ row }) => {
        const isBumpOverdue =
          row.original.bump_due_day_num != null && row.original.bump_due_day_num < 0;

        const textStyleLocal = isBumpOverdue
          ? {
              color: theme.colors.status.danger.fill.surface,
              fontWeight: 700 as const,
            }
          : textStyle;

        const bumpInterval: number[] | undefined =
          row.original.bump_interval_array ??
          (Array.isArray(row.original.bump_interval)
            ? row.original.bump_interval
            : row.original.bump_interval != null
              ? [row.original.bump_interval]
              : undefined);

        return (
          <span style={{ ...textStyleLocal, display: 'block', textAlign: 'center' }}>
            {formatDueDateLine(
              row.original.bump_test_due_date,
              row.original.bump_due_day_num,
              bumpInterval
            )}
          </span>
        );
      },
      enableSorting: true,
      enableColumnFilter: true,
      meta: {
        style: {
          textAlign: 'center',
        },
      },
    },
    // ── 마지막 접속 컬럼 ──
    {
      accessorKey: 'last_connect',
      header: () => t(i18nKeys.site.lastConnect),
      cell: ({ row }) => {
        const isCalibrationOverdue = row.original.calibration_due_date
          ? new Date(row.original.calibration_due_date) < new Date()
          : false;

        const textStyleLocal = isCalibrationOverdue
          ? {
              color: theme.colors.status.danger.fill.surface,
              fontWeight: 700 as const,
            }
          : textStyle;

        return (
          <span style={{ ...textStyleLocal, display: 'block', textAlign: 'center' }}>
            {row.original.last_connect ? formatDate(row.original.last_connect) : '-'}
          </span>
        );
      },
      enableSorting: true,
      enableColumnFilter: true,
      meta: {
        style: {
          textAlign: 'center',
        },
      },
    },
    // ── 액션 컬럼 (수정/삭제/상세 토글) ──
    {
      id: 'actions',
      header: () => t(i18nKeys.site.action),
      cell: ({ row }) => (
        <Flex direction="column" align="center">
          <Flex gap={2} align="center" justify="center">
            <IconButton
              data-tour="device-edit"
              onClick={e => {
                e.stopPropagation();
                onEdit(row.original);
              }}
              aria-label={t(i18nKeys.site.edit)}
            >
              <img src="/icons/edit.svg" alt="" width="30" height="30" />
            </IconButton>
            <IconButton
              data-tour="device-delete"
              onClick={e => {
                e.stopPropagation();
                onDelete(row.original);
              }}
              aria-label={t(i18nKeys.site.delete)}
            >
              <img src="/icons/delete.svg" alt="" width="30" height="30" />
            </IconButton>
          </Flex>
          <TextButton
            data-tour="device-detail-expand"
            onClick={e => {
              e.stopPropagation();
              row.toggleExpanded();
            }}
          >
            {row.getIsExpanded() ? 'Hide Details' : 'Show Details'}
          </TextButton>
        </Flex>
      ),
      enableSorting: false,
      enableColumnFilter: false,
    },
  ];
}

// ==================== 서브 컴포넌트 ====================

/**
 * 확장된 Row에 표시할 컨텐츠 - Detail 정보만 표시
 */
export function DeviceExpandedRow({ device }: { device: DeviceTableRow }) {
  return <DeviceDetailTab device={device as any} />;
}
